'use client';
import * as React from 'react';
import { motion, useReducedMotion, type Variants } from 'framer-motion';
import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';

/**
 * Scroll-in motion primitives. The whole point of these is that entrance
 * animation should be *one decision, applied consistently* — not hand-rolled
 * per section with slightly different easings and distances (which is what
 * makes a page feel busy instead of composed).
 *
 * Every primitive here honours `prefers-reduced-motion`: when the visitor asks
 * for less motion, elements render in their final state with no transform, so
 * nothing moves. Do NOT reach for raw <motion.div> in a page without the same
 * guard — use these.
 *
 * Reveal      — one element fades + rises as it scrolls into view.
 * Stagger     — a container whose direct <StaggerItem> children come in one
 *               after another (a gallery, a service grid, a list of cards).
 * StaggerItem — a child of <Stagger>.
 *
 * Keep it as seasoning (see docs/design.md §5): an opening line, a section
 * heading, a grid of cards. Not every paragraph.
 */

// motion(Box) keeps MUI's `sx` while adding framer-motion props. We expose only
// `sx`/`className` on the public API (all a layout wrapper needs), which also
// sidesteps framer-motion's event-handler type clash with the full BoxProps set.
const MotionBox = motion.create(Box);

const EASE = [0.22, 1, 0.36, 1] as const; // ease-out, gentle overshoot-free

type Direction = 'up' | 'down' | 'left' | 'right' | 'none';

interface CommonProps {
  children: React.ReactNode;
  sx?: SxProps<Theme>;
  className?: string;
}

function offset(direction: Direction, distance: number) {
  switch (direction) {
    case 'up':
      return { y: distance };
    case 'down':
      return { y: -distance };
    case 'left':
      return { x: distance };
    case 'right':
      return { x: -distance };
    default:
      return {};
  }
}

export function Reveal({
  children,
  direction = 'up',
  distance = 24,
  delay = 0,
  duration = 0.6,
  once = true,
  amount = 0.3,
  sx,
  className,
}: CommonProps & {
  /** Where the element travels *from* as it appears. */
  direction?: Direction;
  /** Travel distance in px. Keep small (16–32); big slides feel cheap. */
  distance?: number;
  delay?: number;
  duration?: number;
  /** Animate only the first time it enters (true) or every time (false). */
  once?: boolean;
  /** Fraction of the element that must be visible to trigger (0–1). */
  amount?: number;
}) {
  const reduced = useReducedMotion();

  if (reduced) {
    return (
      <Box sx={sx} className={className}>
        {children}
      </Box>
    );
  }

  return (
    <MotionBox
      sx={sx}
      className={className}
      initial={{ opacity: 0, ...offset(direction, distance) }}
      whileInView={{ opacity: 1, x: 0, y: 0 }}
      viewport={{ once, amount }}
      transition={{ duration, ease: EASE, delay }}
    >
      {children}
    </MotionBox>
  );
}

export function Stagger({
  children,
  gap = 0.08,
  delay = 0,
  once = true,
  amount = 0.2,
  sx,
  className,
}: CommonProps & {
  /** Seconds between each child appearing. */
  gap?: number;
  delay?: number;
  once?: boolean;
  amount?: number;
}) {
  const reduced = useReducedMotion();

  if (reduced) {
    return (
      <Box sx={sx} className={className}>
        {children}
      </Box>
    );
  }

  const container: Variants = {
    hidden: {},
    show: {
      transition: { staggerChildren: gap, delayChildren: delay },
    },
  };

  return (
    <MotionBox
      sx={sx}
      className={className}
      variants={container}
      initial="hidden"
      whileInView="show"
      viewport={{ once, amount }}
    >
      {children}
    </MotionBox>
  );
}

export function StaggerItem({
  children,
  direction = 'up',
  distance = 24,
  duration = 0.6,
  sx,
  className,
}: CommonProps & {
  direction?: Direction;
  distance?: number;
  duration?: number;
}) {
  const reduced = useReducedMotion();

  if (reduced) {
    return (
      <Box sx={sx} className={className}>
        {children}
      </Box>
    );
  }

  const item: Variants = {
    hidden: { opacity: 0, ...offset(direction, distance) },
    show: {
      opacity: 1,
      x: 0,
      y: 0,
      transition: { duration, ease: EASE },
    },
  };

  return (
    <MotionBox sx={sx} className={className} variants={item}>
      {children}
    </MotionBox>
  );
}

export default Reveal;
